import is from '@sindresorhus/is';
import { z } from 'zod';

import {
    type LanguageCode,
    type LanguageCodeForJSA,
    languageCodeSchema,
    languageCodeSchemaForJSA,
} from '~/shared/internationalization';

import type { UrlTilePayload } from '../url-tiles';
import { withCommentsOpen } from '../url-tiles/comments-group';

export const JSA_PRIMARY_DETAIL_TILE_SLOT_KEY = 'jsa.primary-detail';
export const JSA_COMPARE_TILE_SLOT_KEY = 'jsa.compare';
export const JSA_CHANGELOG_TILE_SLOT_KEY = 'jsa.changelog';
export const JSA_ARCHIVE_TILE_SLOT_KEY = 'jsa.archive';
export const JSA_LEVEL_COMPARISON_TILE_SLOT_KEY = 'jsa.level-comparison';
export const JSA_ROLE_COMPARISON_TILE_SLOT_KEY = 'jsa.role-comparison';
export const JSA_PROFILE_DETAIL_TILE_TYPE = 'v1.jsa.profile.detail';
export const JSA_FAMILY_DETAIL_TILE_TYPE = 'v1.jsa.family.detail';
export const JSA_CLUSTER_DETAIL_TILE_TYPE = 'v1.jsa.cluster.detail';
export const JSA_COMPARE_TILE_TYPE = 'v1.jsa.compare';
export const JSA_CHANGELOG_TILE_TYPE = 'v1.jsa.changelog';
export const JSA_ARCHIVE_TILE_TYPE = 'v1.jsa.archive';
export const JSA_LEVEL_COMPARISON_TILE_TYPE = 'v1.jsa.level-comparison';
export const JSA_ROLE_COMPARISON_TILE_TYPE = 'v1.jsa.role-comparison';
export const JSA_CHANGELOG_TILE_WIDTH = '500px';
export const JSA_ARCHIVE_TILE_MAX_WIDTH = '800px';
export const JSA_LEVEL_COMPARISON_TILE_WIDTH = '75%';
export const JSA_ROLE_COMPARISON_TILE_WIDTH = '75%';
export const JSA_ROLE_COMPARISON_MAX_ROLES = 4;

const jsaItemTypeSchema = z.enum(['job-level', 'job-role']);
const jsaModeSchema = z.enum(['edit', 'view']);

export const jsaProfileDetailTileSchema = z.object({
    architectureId: z.uuid(),
    isArchived: z.boolean().optional(),
    isCommentsOpen: z.boolean().optional(),
    itemType: jsaItemTypeSchema,
    mode: jsaModeSchema,
    notificationRow: z.string().min(1).optional(),
    preferredLanguage: z.string().optional(),
    profileId: z.uuid(),
    type: z.literal(JSA_PROFILE_DETAIL_TILE_TYPE),
    version: z.number().int().positive().optional(),
});

export const jsaFamilyDetailTileSchema = z.object({
    architectureId: z.uuid(),
    familyId: z.uuid(),
    mode: jsaModeSchema,
    notificationRow: z.string().min(1).optional(),
    preferredLanguage: languageCodeSchema.optional(),
    type: z.literal(JSA_FAMILY_DETAIL_TILE_TYPE),
});

export const jsaClusterDetailTileSchema = z.object({
    architectureId: z.uuid(),
    clusterId: z.uuid(),
    familyId: z.uuid(),
    mode: jsaModeSchema,
    notificationRow: z.string().min(1).optional(),
    preferredLanguage: languageCodeSchema.optional(),
    type: z.literal(JSA_CLUSTER_DETAIL_TILE_TYPE),
});

// Sentinel versionB value for the frozen change-proposal side of the compare tile.
export const JSA_COMPARE_PROPOSAL_VERSION = 'proposal';

export const jsaCompareTileSchema = z.object({
    architectureId: z.uuid(),
    itemType: jsaItemTypeSchema,
    preferredLanguage: z.string().optional(),
    profileId: z.uuid(),
    proposalBlockId: z.uuid().optional(),
    type: z.literal(JSA_COMPARE_TILE_TYPE),
    versionA: z.string().min(1),
    versionB: z.string().min(1),
});

export const jsaChangelogTileSchema = z.object({
    architectureId: z.uuid(),
    preferredLanguage: languageCodeSchema.optional(),
    type: z.literal(JSA_CHANGELOG_TILE_TYPE),
});

export const jsaArchiveTileSchema = z.object({
    architectureId: z.uuid(),
    type: z.literal(JSA_ARCHIVE_TILE_TYPE),
});

export const jsaLevelComparisonTileSchema = z.object({
    architectureId: z.uuid(),
    preferredLanguage: languageCodeSchemaForJSA.optional(),
    roleId: z.uuid(),
    showProficiency: z.boolean().optional(),
    type: z.literal(JSA_LEVEL_COMPARISON_TILE_TYPE),
});

// Compares sibling profiles of one kind: either job roles or job levels, never mixed.
export const jsaRoleComparisonTileSchema = z.object({
    architectureId: z.uuid(),
    itemIds: z.array(z.uuid()).min(1).max(JSA_ROLE_COMPARISON_MAX_ROLES),
    itemType: jsaItemTypeSchema,
    preferredLanguage: languageCodeSchemaForJSA.optional(),
    showProficiency: z.boolean().optional(),
    type: z.literal(JSA_ROLE_COMPARISON_TILE_TYPE),
});

export type JsaProfileDetailTilePayload = z.infer<typeof jsaProfileDetailTileSchema>;
export type JsaFamilyDetailTilePayload = z.infer<typeof jsaFamilyDetailTileSchema>;
export type JsaClusterDetailTilePayload = z.infer<typeof jsaClusterDetailTileSchema>;
export type JsaCompareTilePayload = z.infer<typeof jsaCompareTileSchema>;
export type JsaChangelogTilePayload = z.infer<typeof jsaChangelogTileSchema>;
export type JsaArchiveTilePayload = z.infer<typeof jsaArchiveTileSchema>;
export type JsaLevelComparisonTilePayload = z.infer<typeof jsaLevelComparisonTileSchema>;
export type JsaRoleComparisonTilePayload = z.infer<typeof jsaRoleComparisonTileSchema>;
export type JsaPrimaryDetailTilePayload =
    | JsaClusterDetailTilePayload
    | JsaFamilyDetailTilePayload
    | JsaProfileDetailTilePayload;

export const resolveJsaLevelComparisonShowProficiency = (
    tileValue: JsaLevelComparisonTilePayload['showProficiency'],
    persistedValue: boolean | undefined,
    fallbackValue = true,
): boolean => tileValue ?? persistedValue ?? fallbackValue;

export const createJsaProfileDetailTilePayload = ({
    architectureId,
    isArchived,
    isCommentsOpen,
    itemType,
    mode,
    notificationRow,
    preferredLanguage,
    profileId,
    version,
}: {
    readonly architectureId: string;
    readonly isArchived?: boolean;
    readonly isCommentsOpen?: boolean;
    readonly itemType: 'job-level' | 'job-role';
    readonly mode: 'edit' | 'view';
    readonly notificationRow?: string;
    readonly preferredLanguage?: LanguageCodeForJSA;
    readonly profileId: string;
    readonly version?: number;
}): JsaProfileDetailTilePayload => ({
    architectureId,
    itemType,
    mode,
    ...(!is.undefined(isArchived) ? { isArchived } : {}),
    ...(!is.undefined(isCommentsOpen) ? { isCommentsOpen } : {}),
    ...(!is.undefined(notificationRow) ? { notificationRow } : {}),
    ...(!is.undefined(preferredLanguage) ? { preferredLanguage } : {}),
    profileId,
    type: JSA_PROFILE_DETAIL_TILE_TYPE,
    ...(!is.undefined(version) ? { version } : {}),
});

export const withJsaProfileCommentsOpen = (
    payload: JsaProfileDetailTilePayload,
    isCommentsOpen: boolean,
): JsaProfileDetailTilePayload => withCommentsOpen(payload, isCommentsOpen);

export const createJsaFamilyDetailTilePayload = ({
    architectureId,
    familyId,
    mode,
    notificationRow,
    preferredLanguage,
}: {
    readonly architectureId: string;
    readonly familyId: string;
    readonly mode: 'edit' | 'view';
    readonly notificationRow?: string;
    readonly preferredLanguage?: LanguageCode;
}): JsaFamilyDetailTilePayload => ({
    architectureId,
    familyId,
    mode,
    ...(!is.undefined(notificationRow) ? { notificationRow } : {}),
    ...(!is.undefined(preferredLanguage) ? { preferredLanguage } : {}),
    type: JSA_FAMILY_DETAIL_TILE_TYPE,
});

export const createJsaClusterDetailTilePayload = ({
    architectureId,
    clusterId,
    familyId,
    mode,
    notificationRow,
    preferredLanguage,
}: {
    readonly architectureId: string;
    readonly clusterId: string;
    readonly familyId: string;
    readonly mode: 'edit' | 'view';
    readonly notificationRow?: string;
    readonly preferredLanguage?: LanguageCode;
}): JsaClusterDetailTilePayload => ({
    architectureId,
    clusterId,
    familyId,
    mode,
    ...(!is.undefined(notificationRow) ? { notificationRow } : {}),
    ...(!is.undefined(preferredLanguage) ? { preferredLanguage } : {}),
    type: JSA_CLUSTER_DETAIL_TILE_TYPE,
});

export const createJsaCompareTilePayload = ({
    architectureId,
    itemType,
    preferredLanguage,
    profileId,
    proposalBlockId,
    versionA,
    versionB,
}: {
    readonly architectureId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly preferredLanguage?: string;
    readonly profileId: string;
    readonly proposalBlockId?: string;
    readonly versionA: string;
    readonly versionB: string;
}): JsaCompareTilePayload => ({
    architectureId,
    itemType,
    ...(!is.undefined(preferredLanguage) ? { preferredLanguage } : {}),
    profileId,
    ...(!is.undefined(proposalBlockId) ? { proposalBlockId } : {}),
    type: JSA_COMPARE_TILE_TYPE,
    versionA,
    versionB,
});

export const createJsaChangelogTilePayload = ({
    architectureId,
    preferredLanguage,
}: {
    readonly architectureId: string;
    readonly preferredLanguage?: LanguageCode;
}): JsaChangelogTilePayload => ({
    architectureId,
    ...(!is.undefined(preferredLanguage) ? { preferredLanguage } : {}),
    type: JSA_CHANGELOG_TILE_TYPE,
});

export const createJsaArchiveTilePayload = ({
    architectureId,
}: {
    readonly architectureId: string;
}): JsaArchiveTilePayload => ({
    architectureId,
    type: JSA_ARCHIVE_TILE_TYPE,
});

export const createJsaLevelComparisonTilePayload = ({
    architectureId,
    preferredLanguage,
    roleId,
    showProficiency,
}: {
    readonly architectureId: string;
    readonly preferredLanguage?: LanguageCodeForJSA;
    readonly roleId: string;
    readonly showProficiency?: boolean;
}): JsaLevelComparisonTilePayload => ({
    architectureId,
    ...(!is.undefined(preferredLanguage) ? { preferredLanguage } : {}),
    roleId,
    ...(!is.undefined(showProficiency) ? { showProficiency } : {}),
    type: JSA_LEVEL_COMPARISON_TILE_TYPE,
});

export const createJsaRoleComparisonTilePayload = ({
    architectureId,
    itemIds,
    itemType,
    preferredLanguage,
    showProficiency,
}: {
    readonly architectureId: string;
    readonly itemIds: readonly string[];
    readonly itemType: 'job-level' | 'job-role';
    readonly preferredLanguage?: LanguageCodeForJSA;
    readonly showProficiency?: boolean;
}): JsaRoleComparisonTilePayload => ({
    architectureId,
    itemIds: [...itemIds],
    itemType,
    ...(!is.undefined(preferredLanguage) ? { preferredLanguage } : {}),
    ...(!is.undefined(showProficiency) ? { showProficiency } : {}),
    type: JSA_ROLE_COMPARISON_TILE_TYPE,
});

export const isJsaProfileDetailTilePayload = (payload: unknown): payload is JsaProfileDetailTilePayload =>
    is.plainObject(payload) && payload['type'] === JSA_PROFILE_DETAIL_TILE_TYPE;

export const selectJsaPrimaryDetailIdFromTile = (payload: UrlTilePayload): string | undefined => {
    switch (payload.type) {
        case JSA_FAMILY_DETAIL_TILE_TYPE:
            return is.string(payload['familyId']) ? payload['familyId'] : undefined;
        case JSA_CLUSTER_DETAIL_TILE_TYPE:
            return is.string(payload['clusterId']) ? payload['clusterId'] : undefined;
        case JSA_PROFILE_DETAIL_TILE_TYPE:
            return is.string(payload['profileId']) ? payload['profileId'] : undefined;
        default:
            return undefined;
    }
};

export const selectJsaPrimaryDetailNotificationRowFromTile = (payload: UrlTilePayload): string | undefined => {
    switch (payload.type) {
        case JSA_FAMILY_DETAIL_TILE_TYPE:
        case JSA_CLUSTER_DETAIL_TILE_TYPE:
        case JSA_PROFILE_DETAIL_TILE_TYPE:
            return is.nonEmptyString(payload['notificationRow']) ? payload['notificationRow'] : undefined;
        default:
            return undefined;
    }
};

export const isJsaCompareTilePayload = (payload: unknown): payload is JsaCompareTilePayload =>
    is.plainObject(payload) && payload['type'] === JSA_COMPARE_TILE_TYPE;

export const isJsaLevelComparisonTilePayload = (payload: unknown): payload is JsaLevelComparisonTilePayload =>
    is.plainObject(payload) && payload['type'] === JSA_LEVEL_COMPARISON_TILE_TYPE;

export const isJsaRoleComparisonTilePayload = (payload: unknown): payload is JsaRoleComparisonTilePayload =>
    is.plainObject(payload) && payload['type'] === JSA_ROLE_COMPARISON_TILE_TYPE;
